import Fuse from "fuse.js";

/** What a palette row can be found by. */
export type MarketSearchKeys = {
  /** The row's display label, e.g. "BTC-USD" or "BTC-USDC" (instrumentLabel). */
  label: string;
  asset: string;
  displayName?: string;
  category?: string;
};

/**
 * A query with no letter or digit in it ("<", "-", "  ") names no market. Fuse
 * matched those against every label containing a dash, so "<script>" or "-"
 * listed half the book.
 */
export const isSearchableQuery = (query: string): boolean =>
  /[a-z0-9]/i.test(query);

const lower = (value?: string) => (value || "").toLowerCase();

/**
 * One ranking over every row the palette lists, whatever its vertical.
 *
 * Perp and spot rows used to be searched separately — perps by Fuse over
 * asset/display name, spot by a substring test on the asset alone — so
 * "BTC-USDC" or "usdc" found no spot market at all (its label was never a
 * key), and "EUR" ranked ETH above EUR because both were fuzzy hits of equal
 * standing. Rows are now ranked in tiers: an exact label/asset match, then a
 * prefix, then a substring of any key; only when nothing matches directly do
 * fuzzy matches (by score) stand in. Within a tier the incoming order is kept.
 */
export function rankMarketRows<T>(
  rows: T[],
  query: string,
  keysOf: (row: T) => MarketSearchKeys
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  if (!isSearchableQuery(q)) return [];

  const tiers: T[][] = [[], [], []];
  const rest: { row: T; keys: MarketSearchKeys }[] = [];

  rows.forEach((row) => {
    const keys = keysOf(row);
    const primary = [lower(keys.label), lower(keys.asset)];
    const all = [...primary, lower(keys.displayName), lower(keys.category)];
    if (primary.some((k) => k === q)) {
      tiers[0].push(row);
    } else if (primary.some((k) => k.startsWith(q))) {
      tiers[1].push(row);
    } else if (all.some((k) => k.includes(q))) {
      tiers[2].push(row);
    } else {
      rest.push({ row, keys });
    }
  });

  const direct = [...tiers[0], ...tiers[1], ...tiers[2]];
  // Fuzzy matching is the fallback for typos ("etherum"), not padding: once a
  // query matches something directly, near-misses ("usdc" ~ "BTC-USD") would
  // only bury the answer.
  if (direct.length) return direct;

  return new Fuse(rest, {
    keys: ["keys.label", "keys.asset", "keys.displayName", "keys.category"],
    shouldSort: true,
    threshold: 0.4,
  })
    .search(q)
    .map((result) => result.item.row);
}
